import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { 
  LineChart as LineChartIcon, BarChart3, PieChart, TrendingUp, TrendingDown,
  Calendar, Download, Filter, Eye, MousePointer, Users, ShoppingCart,
  DollarSign, Target, Clock, ArrowUpRight, ArrowDownRight, Loader2
} from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart as RePieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const timeRanges = ['7 days', '30 days', '90 days', '12 months'];

const COLORS = ['#34d399', '#f472b6', '#60a5fa', '#E36559', '#5D6973'];

export default function Analytics() {
  const { token } = useAuth();
  const [selectedRange, setSelectedRange] = useState('30 days');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  // Generate data based on selected range
  const generateTrafficData = (range) => {
    const days = range === '7 days' ? 7 : range === '30 days' ? 30 : range === '90 days' ? 90 : 365;
    const data = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      // Skip some days for longer ranges to avoid cluttered charts
      if ((range === '90 days' && i % 3 !== 0) || (range === '12 months' && i % 7 !== 0)) {
        continue;
      }
      
      const baseOrganic = 4000 + Math.random() * 2000;
      const basePaid = 2500 + Math.random() * 1500;
      const baseSocial = 1200 + Math.random() * 1000;
      const baseDirect = 700 + Math.random() * 500;
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        organic: Math.round(baseOrganic),
        paid: Math.round(basePaid),
        social: Math.round(baseSocial),
        direct: Math.round(baseDirect)
      });
    }
    return data;
  };

  const [trafficData, setTrafficData] = useState(() => generateTrafficData('30 days'));

  const conversionData = [
    { name: 'Visitors', value: 125400 },
    { name: 'Leads', value: 18750 },
    { name: 'MQLs', value: 5620 },
    { name: 'SQLs', value: 2810 },
    { name: 'Customers', value: 1125 },
  ];

  const channelPerformance = [
    { channel: 'Email', conversions: 450, revenue: 45000, roi: 320 },
    { channel: 'Social Media', conversions: 320, revenue: 32000, roi: 280 },
    { channel: 'Paid Search', conversions: 280, revenue: 28000, roi: 240 },
    { channel: 'Organic', conversions: 520, revenue: 52000, roi: 450 },
    { channel: 'Referral', conversions: 180, revenue: 18000, roi: 380 },
  ];

  const audienceData = [
    { name: '18-24', value: 15 },
    { name: '25-34', value: 35 },
    { name: '35-44', value: 28 },
    { name: '45-54', value: 15 },
    { name: '55+', value: 7 },
  ];

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  useEffect(() => {
    // Update traffic data when range changes
    setTrafficData(generateTrafficData(selectedRange));
  }, [selectedRange]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API}/api/marketing/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRangeChange = (range) => {
    setSelectedRange(range);
    toast.success(`Showing data for ${range}`);
  };

  const handleExport = async () => {
    setExporting(true);
    
    try {
      // Compile all data for export
      const exportData = {
        report_date: new Date().toISOString(),
        time_range: selectedRange,
        metrics: {
          total_visitors: '125,400',
          page_views: '458,200',
          conversion_rate: '3.8%',
          avg_session: '4m 32s',
          revenue: '$175,000'
        },
        traffic_sources: trafficData,
        conversion_funnel: conversionData,
        channel_performance: channelPerformance,
        audience_demographics: audienceData
      };

      // Generate CSV content
      let csvContent = "Marketing Analytics Report\n";
      csvContent += `Generated: ${new Date().toLocaleDateString()}\n`;
      csvContent += `Time Range: ${selectedRange}\n\n`;
      
      // Metrics Section
      csvContent += "KEY METRICS\n";
      csvContent += "Metric,Value\n";
      csvContent += "Total Visitors,125400\n";
      csvContent += "Page Views,458200\n";
      csvContent += "Conversion Rate,3.8%\n";
      csvContent += "Average Session,4m 32s\n";
      csvContent += "Revenue,$175000\n\n";
      
      // Traffic Sources
      csvContent += "TRAFFIC SOURCES\n";
      csvContent += "Date,Organic,Paid,Social,Direct\n";
      trafficData.forEach(row => {
        csvContent += `${row.date},${row.organic},${row.paid},${row.social},${row.direct}\n`;
      });
      csvContent += "\n";
      
      // Channel Performance
      csvContent += "CHANNEL PERFORMANCE\n";
      csvContent += "Channel,Conversions,Revenue,ROI %\n";
      channelPerformance.forEach(row => {
        csvContent += `${row.channel},${row.conversions},$${row.revenue},${row.roi}%\n`;
      });
      csvContent += "\n";
      
      // Conversion Funnel
      csvContent += "CONVERSION FUNNEL\n";
      csvContent += "Stage,Value\n";
      conversionData.forEach(row => {
        csvContent += `${row.name},${row.value}\n`;
      });
      csvContent += "\n";
      
      // Audience Demographics
      csvContent += "AUDIENCE DEMOGRAPHICS\n";
      csvContent += "Age Group,Percentage\n";
      audienceData.forEach(row => {
        csvContent += `${row.name},${row.value}%\n`;
      });

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `marketing_analytics_${selectedRange.replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Analytics report exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export analytics');
    } finally {
      setExporting(false);
    }
  };

  // Calculate metrics based on range multiplier
  const getRangeMultiplier = () => {
    switch (selectedRange) {
      case '7 days': return 0.23;
      case '30 days': return 1;
      case '90 days': return 3;
      case '12 months': return 12;
      default: return 1;
    }
  };

  const multiplier = getRangeMultiplier();

  const metrics = [
    { 
      label: 'Total Visitors', 
      value: `${(125.4 * multiplier).toFixed(1)}K`, 
      change: '+18.6%', 
      positive: true,
      icon: Eye,
      color: 'text-primary'
    },
    { 
      label: 'Page Views', 
      value: `${(458.2 * multiplier).toFixed(1)}K`, 
      change: '+25.4%', 
      positive: true,
      icon: MousePointer,
      color: 'text-emerald-400'
    },
    { 
      label: 'Conversion Rate', 
      value: '3.8%', 
      change: '+0.5%', 
      positive: true,
      icon: Target,
      color: 'text-primary'
    },
    { 
      label: 'Avg. Session', 
      value: '4m 32s', 
      change: '-12s', 
      positive: false,
      icon: Clock,
      color: 'text-emerald-400'
    },
    { 
      label: 'Revenue', 
      value: `$${(175 * multiplier).toFixed(0)}K`, 
      change: '+32.1%', 
      positive: true,
      icon: DollarSign,
      color: 'text-primary'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <LineChartIcon className="w-6 h-6 text-primary" />
            Analytics
          </h1>
          <p className="text-muted-foreground">Track your marketing performance and insights</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-secondary rounded-lg p-1">
            {timeRanges.map(range => (
              <button
                key={range}
                onClick={() => handleRangeChange(range)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  selectedRange === range 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-secondary/80'
                }`}
                data-testid={`range-${range.replace(' ', '-')}`}
              >
                {range}
              </button>
            ))}
          </div>
          <button 
            onClick={handleExport}
            disabled={exporting}
            className="marketing-btn-secondary flex items-center gap-2" 
            data-testid="export-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>{exporting ? 'Exporting...' : 'Export'}</span>
          </button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {metrics.map((metric, index) => (
          <div key={index} className="metric-card" data-testid={`metric-${index}`}>
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg bg-secondary flex items-center justify-center ${metric.color}`}>
                <metric.icon className="w-5 h-5" />
              </div>
              <div className={`flex items-center gap-1 text-xs font-medium ${metric.positive ? 'text-emerald-400' : 'text-red-400'}`}>
                {metric.positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {metric.change}
              </div>
            </div>
            <p className="text-2xl font-bold">{metric.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{metric.label}</p>
          </div>
        ))}
      </div>

      {/* Traffic Sources Chart */}
      <div className="marketing-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-semibold">Traffic Sources</h3>
            <p className="text-sm text-muted-foreground">Breakdown by channel over time ({selectedRange})</p>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary"></div>
              <span className="text-muted-foreground">Organic</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-400"></div>
              <span className="text-muted-foreground">Paid</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-pink-400"></div>
              <span className="text-muted-foreground">Social</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
              <span className="text-muted-foreground">Direct</span>
            </div>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trafficData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(30 10% 20%)" />
              <XAxis dataKey="date" stroke="hsl(40 15% 55%)" fontSize={12} />
              <YAxis stroke="hsl(40 15% 55%)" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  background: 'hsl(30 12% 12%)', 
                  border: '1px solid hsl(30 10% 20%)',
                  borderRadius: '8px',
                  color: 'hsl(40 20% 90%)'
                }} 
              />
              <Area type="monotone" dataKey="organic" stackId="1" stroke="#EEC785" fill="#EEC785" fillOpacity={0.6} />
              <Area type="monotone" dataKey="paid" stackId="1" stroke="#60a5fa" fill="#60a5fa" fillOpacity={0.6} />
              <Area type="monotone" dataKey="social" stackId="1" stroke="#f472b6" fill="#f472b6" fillOpacity={0.6} />
              <Area type="monotone" dataKey="direct" stackId="1" stroke="#34d399" fill="#34d399" fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Second Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Conversion Funnel */}
        <div className="marketing-card">
          <h3 className="font-semibold mb-4">Conversion Funnel</h3>
          <div className="space-y-3">
            {conversionData.map((item, index) => {
              const width = (item.value / conversionData[0].value) * 100;
              const prevValue = index > 0 ? conversionData[index - 1].value : item.value;
              const dropoff = index > 0 ? ((prevValue - item.value) / prevValue * 100).toFixed(1) : 0;
              
              return (
                <div key={index}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>{item.name}</span>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{item.value.toLocaleString()}</span>
                      {index > 0 && (
                        <span className="text-xs text-red-400">-{dropoff}%</span>
                      )}
                    </div>
                  </div>
                  <div className="h-8 bg-secondary rounded-lg overflow-hidden">
                    <div 
                      className="h-full rounded-lg transition-all duration-500"
                      style={{ 
                        width: `${width}%`,
                        backgroundColor: COLORS[index]
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Audience Demographics */}
        <div className="marketing-card">
          <h3 className="font-semibold mb-4">Audience Demographics</h3>
          <div className="flex items-center justify-center">
            <div className="w-48 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={audienceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {audienceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(30 12% 12%)', 
                      border: '1px solid hsl(30 10% 20%)',
                      borderRadius: '8px',
                      color: 'hsl(40 20% 90%)'
                    }} 
                  />
                </RePieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 ml-4">
              {audienceData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }}></div>
                  <span className="text-sm">{item.name}: {item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Channel Performance Table */}
      <div className="marketing-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Channel Performance</h3>
          <button 
            onClick={handleExport}
            className="text-sm text-primary flex items-center gap-1 hover:underline"
          >
            <Download className="w-4 h-4" />
            Export Table
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Channel</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Conversions</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Revenue</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">ROI</th>
              </tr>
            </thead>
            <tbody>
              {channelPerformance.map((channel, index) => (
                <tr key={index} className="border-b border-border/50 hover:bg-secondary/30 transition-colors" data-testid={`channel-row-${index}`}>
                  <td className="py-3 px-4 font-medium">{channel.channel}</td>
                  <td className="py-3 px-4 text-right">{channel.conversions}</td>
                  <td className="py-3 px-4 text-right text-primary">${channel.revenue.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right">
                    <span className="text-emerald-400">{channel.roi}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
